import React from 'react';
import { motion } from 'framer-motion';
import { Award, Medal, Crown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

function ReferralTierStepper({ totalReferrals = 0, className = '' }) {
  const { t, formatMessage } = useLanguage();
  const tiers = t.enrola.tiers;

  const TIERS = [
    { key: 'bronze', label: tiers.bronze, referrals: 1, Icon: Award, color: 'from-amber-700 to-amber-600' },
    { key: 'silver', label: tiers.silver, referrals: 3, Icon: Medal, color: 'from-slate-400 to-slate-500' },
    { key: 'gold', label: tiers.gold, referrals: 5, Icon: Crown, color: 'from-yellow-500 to-amber-500' },
  ];

  const maxTier = TIERS[TIERS.length - 1];
  const progress = Math.min(totalReferrals / maxTier.referrals, 1);

  return (
    <div className={`stepper-shell ${className}`}>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="eyebrow">{tiers.gamification}</p>
          <h3 className="text-lg font-bold tracking-tight text-secondary-900 ">
            {tiers.levelUpTitle}
          </h3>
          <p className="mt-1 text-sm text-secondary-600 ">
            {formatMessage(tiers.progressSummary, { count: totalReferrals, goal: maxTier.referrals })}
          </p>
        </div>
      </div>

      <div className="relative mb-8 px-1">
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-secondary-100 " />
        <motion.div
          className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-400 shadow-[0_0_14px_rgba(249,115,22,0.35)]"
          initial={{ width: 0 }}
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
        <div className="relative flex justify-between">
          {TIERS.map((tier, i) => {
            const reached = totalReferrals >= tier.referrals;
            const Icon = tier.Icon;
            return (
              <div key={tier.key} className="flex flex-col items-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.15 * i }}
                  className={
                    reached
                      ? `icon-tile relative z-10 h-12 w-12 bg-gradient-to-br ${tier.color} ring-2 ring-white/50`
                      : 'relative z-10 flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-secondary-200 bg-white text-secondary-400 shadow-soft   '
                  }
                >
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </motion.div>
                <span
                  className={`mt-2 text-center text-xs font-semibold ${
                    reached ? 'text-secondary-900 ' : 'text-secondary-400'
                  }`}
                >
                  {tier.label}
                </span>
                <span className="text-2xs text-secondary-500 ">
                  {formatMessage(tiers.referralsShort, { count: tier.referrals })}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ReferralTierStepper;
